<script setup lang="ts">
import type { MatrixCell, MatrixColumn, MatrixRow } from '../../../util/answerRecapMatrix';
import useLocale from '../../../../composables/use-locale';
import { cellColor } from './cellColor';

defineProps<{
  rows: MatrixRow[];
  columns: MatrixColumn[];
}>();

const emit = defineEmits<{
  (e: 'openQuestion', questionIndex: number): void;
  (e: 'openPlayer', playerId: string): void;
}>();

const { t } = useLocale();

const pctTone = (pct: number) =>
  pct >= 70 ? 'text-green-darken-2' : pct >= 50 ? 'on-surface-medium' : 'text-highlighterred-darken-1';

// Verdict in text as well as colour, for grayscale, print and screen readers.
const cellAriaLabel = (row: MatrixRow, column: MatrixColumn, cell: MatrixCell) => {
  const base = t('results.answer_recap.cell_aria', { player: row.label, question: column.questionIndex + 1 });
  if (cell.state === 'none') {
    return `${base}, ${t('results.answer_recap.no_answer')}`;
  }
  return [
    base,
    t('results.answer_recap.cell_aria_chose', { letter: cell.letter }),
    cell.state === 'correct' ? t('results.answer_recap.correct') : t('results.answer_recap.incorrect'),
    t('results.answer_recap.cell_aria_correct_answer', { letter: column.correctLetter }),
  ].join(', ');
};
</script>

<template>
  <table class="matrix-table my-auto me-auto">
    <caption class="d-sr-only">
      {{
        t('results.answer_recap.table_caption')
      }}
    </caption>

    <thead>
      <tr>
        <th
          scope="col"
          class="matrix-table__sticky bg-surface ps-4 text-overline text-left text-no-wrap pe-2 border-b-md border-opacity-25"
        >
          {{ t('results.answer_recap.player_column') }}
        </th>
        <th
          v-for="column in columns"
          :key="column.questionId"
          scope="col"
          class="bg-surface border-b-md border-opacity-25"
        >
          <v-btn
            class="question-chip"
            size="x-small"
            variant="text"
            density="compact"
            :title="column.phrase"
            :data-testid="`quizter-results-question-${column.questionIndex + 1}`"
            @click="emit('openQuestion', column.questionIndex)"
          >
            {{ column.questionIndex + 1 }}
          </v-btn>
        </th>
        <th scope="col" class="bg-surface text-overline text-left text-no-wrap ps-2 pe-4 border-b-md border-opacity-25">
          {{ t('results.answer_recap.score_column') }}
        </th>
      </tr>
    </thead>

    <tbody>
      <tr v-for="row in rows" :key="row.playerId">
        <th scope="row" class="matrix-table__sticky bg-surface ps-4 text-left">
          <v-btn
            class="player-button text-none"
            size="small"
            variant="text"
            density="compact"
            :data-testid="`quizter-results-player-${row.playerId}`"
            @click="emit('openPlayer', row.playerId)"
          >
            <v-avatar v-if="row.avatar" :image="row.avatar" size="20" class="me-2" />
            {{ row.label }}
          </v-btn>
        </th>

        <!-- Plain <td>s rather than a component: ~875 of them at the 35 x 25 ceiling. -->
        <td
          v-for="(cell, index) in row.cells"
          :key="columns[index].questionId"
          class="cell text-center rounded-sm"
          :class="`bg-${cellColor[cell.state]}`"
          :aria-label="cellAriaLabel(row, columns[index], cell)"
        >
          {{ cell.letter }}
        </td>

        <td class="matrix-table__number text-center text-no-wrap ps-2 pe-4 on-surface-medium">
          {{ row.correct }}/{{ row.total }}
        </td>
      </tr>
    </tbody>

    <tfoot>
      <tr>
        <th
          scope="row"
          class="matrix-table__sticky bg-surface ps-4 text-overline text-left text-no-wrap pe-2 border-t-md border-opacity-25"
        >
          {{ t('results.answer_recap.pct_correct') }}
        </th>
        <td
          v-for="column in columns"
          :key="column.questionId"
          class="matrix-table__number bg-surface text-center text-no-wrap border-t-md border-opacity-25"
          :class="pctTone(column.pctCorrect)"
          :data-testid="`quizter-results-pct-${column.questionIndex + 1}`"
        >
          {{ column.pctCorrect }}%
        </td>
        <td class="bg-surface pe-4"></td>
      </tr>
    </tfoot>
  </table>
</template>

<style scoped lang="scss">
// A plain table rather than v-table: every default v-table brings (padding, borders, row height)
// would need an !important override for a heatmap.
.matrix-table {
  // The 2px gap turns the header and footer rules into one dash per column.
  border-collapse: separate;
  border-spacing: 2px 1px;

  th,
  td {
    padding: 0;
    height: var(--recap-row-height);
    line-height: var(--recap-row-height);
  }

  thead th {
    height: calc(var(--recap-row-height) + 12px);
    vertical-align: bottom;
    padding-bottom: 5px;
  }

  tfoot th,
  tfoot td {
    height: calc(var(--recap-row-height) + 12px);
    vertical-align: top;
    padding-top: 5px;
  }
}

// Stays put while the question columns scroll sideways underneath.
.matrix-table__sticky {
  position: sticky;
  left: 0;
  z-index: 1;
}

// Stay put while the players scroll past underneath. border-collapse: separate above is what makes
// this work at all: collapsed borders detach from a sticky cell and stay behind with the scroll.
thead th {
  position: sticky;
  top: 0;
  z-index: 2;
}

tfoot th,
tfoot td {
  position: sticky;
  bottom: 0;
  z-index: 2;
}

// The corners anchor on both axes, so they have to paint over either run.
thead .matrix-table__sticky,
tfoot .matrix-table__sticky {
  z-index: 3;
}

/*
 border-spacing leaves a gap beside every pinned cell, and a scrolling cell would show through it as
 a sliver. A shadow in the surface colour fills the gap and, being part of the pinned cell, paints at
 its z-index rather than behind it. The rules below stay separate because a shared
 .matrix-table__sticky declaration would out-specify the thead/tfoot ones and drop their axis.
 */
tbody .matrix-table__sticky {
  box-shadow: 2px 0 0 rgb(var(--v-theme-surface));
}

thead th {
  box-shadow:
    2px 0 0 rgb(var(--v-theme-surface)),
    0 1px 0 rgb(var(--v-theme-surface));
}

tfoot th,
tfoot td {
  box-shadow:
    2px 0 0 rgb(var(--v-theme-surface)),
    0 -1px 0 rgb(var(--v-theme-surface));
}

.cell {
  width: var(--recap-cell-size);
  min-width: var(--recap-cell-size);
  font-size: 18px;
}

// Sized by the row rather than by Vuetify's button sizes, which would leave the text tiny beside the
// cell next to it.
.question-chip,
.player-button {
  height: var(--recap-row-height);
  min-height: var(--recap-row-height);
  font-size: 16px;
}

.matrix-table__number {
  font-size: 15px;
}
</style>
